// contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";
import { supabase } from "../lib/supabaseClient";

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé dans un AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState("viewer");
  const [userSpecialite, setUserSpecialite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [session, setSession] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);

  // 🔥 Flags pour gérer l'initialisation
  const isInitializedRef = useRef(false);
  const isInitializingRef = useRef(false);

  console.log('🎨 AuthProvider RENDER - loading:', loading, 'userRole:', userRole);

  const fetchUserRole = useCallback(async (userId) => {
    if (!userId) {
      console.log("❌ fetchUserRole - Pas d'userId");
      setUserRole("viewer");
      return;
    }

    try {
      console.log("🔍 fetchUserRole - START - userId:", userId);
      
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("role, specialite")
        .eq("id", userId)
        .single();

      console.log("📊 fetchUserRole - Response:", { data: profileData, error: profileError });

      if (profileError) {
        console.error("❌ fetchUserRole - Erreur:", profileError);
        
        // 🔥 SUPPRIMÉ: Ne plus essayer de créer le profil ici
        // Le trigger s'en charge automatiquement
        if (profileError.code === 'PGRST116') {
          console.log("⚠️ Profil pas encore créé par le trigger, utilisation du rôle par défaut");
        }
        
        setUserRole("viewer");
        setUserSpecialite(null);
        return;
      }

      if (profileData?.role) {
        console.log("✅ fetchUserRole - Rôle trouvé:", profileData.role);
        setUserRole(profileData.role);
        setUserSpecialite(profileData?.specialite || null);
      } else {
        console.log("⚠️ fetchUserRole - Pas de rôle dans profileData");
        setUserRole("viewer");
        setUserSpecialite(null);
      }

    } catch (err) {
      console.error("❌ fetchUserRole - Exception:", err);
      setUserRole("viewer");
      setUserSpecialite(null);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    console.log('🔧 useEffect AUTH - Démarrage');

    const initializeAuth = async () => {
      // 🔥 Marquer qu'on est en train d'initialiser
      isInitializingRef.current = true;
      
      try {
        console.log("🚀 Début initialisation auth");

        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        console.log("📍 getSession terminé, userId:", sessionData?.session?.user?.id);

        if (sessionError) {
          console.error("❌ sessionError:", sessionError);
          throw sessionError;
        }

        if (!isMounted) {
          console.log('⚠️ Component unmounted, abort');
          return;
        }

        const currentSession = sessionData?.session;

        if (currentSession?.user) {
          console.log("✅ Session trouvée:", currentSession.user.id);
          setSession(currentSession);
          setUser(currentSession.user);
          setSessionStartTime(new Date());
          
          console.log('🔄 Appel fetchUserRole...');
          await fetchUserRole(currentSession.user.id);
          console.log('✅ fetchUserRole terminé');
        } else {
          console.log("❌ Pas de session active");
          setUser(null);
          setUserRole("viewer");
          setUserSpecialite(null);
        }
        
      } catch (err) {
        console.error("❌ Erreur initialisation auth:", err);
        if (isMounted) {
          setUser(null);
          setUserRole("viewer");
          setUserSpecialite(null);
        }
      } finally {
        if (isMounted) {
          // 🔥 Marquer comme terminé AVANT de setLoading(false)
          isInitializingRef.current = false;
          isInitializedRef.current = true;
          console.log("🏁 FINALLY - isInitialized = true, setLoading(false)");
          setLoading(false);
        }
      }
    };

    // Listener pour les changements d'authentification
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, authSession) => {
        console.log("🔄 Auth event:", event, "| isInitializing:", isInitializingRef.current, "| isInitialized:", isInitializedRef.current);

        if (!isMounted) {
          console.log('⚠️ Auth change mais unmounted');
          return;
        }

        // 🔥 IGNORER TOUS LES ÉVÉNEMENTS PENDANT L'INITIALISATION
        if (isInitializingRef.current) {
          console.log("⏭️ Ignore", event, "- en cours d'initialisation");
          return;
        }

        // 🔥 Ignorer INITIAL_SESSION (toujours géré par initializeAuth)
        if (event === "INITIAL_SESSION") {
          console.log("⏭️ Ignore INITIAL_SESSION");
          return;
        }

        // 🔥 Ignorer SIGNED_IN si déjà initialisé (page refresh)
        if (event === "SIGNED_IN" && isInitializedRef.current) {
          console.log("⏭️ Ignore SIGNED_IN - déjà initialisé");
          return;
        }

        // 🔥 Ignorer TOKEN_REFRESHED
        if (event === "TOKEN_REFRESHED") {
          console.log("⏭️ Ignore TOKEN_REFRESHED");
          return;
        }

        // Gérer les vrais événements
        if (authSession?.user) {
          console.log("👤 Traitement event:", event);
          
          if (event === "SIGNED_IN") {
            console.log("🔄 Nouveau login détecté");
            setLoading(true);
            setUser(authSession.user);
            setSession(authSession);
            setSessionStartTime(new Date());
            await fetchUserRole(authSession.user.id);
            setLoading(false);
            isInitializedRef.current = true;
          }
        } else if (event === "SIGNED_OUT") {
          console.log("👋 Déconnexion détectée");
          setUser(null);
          setSession(null);
          setUserRole("viewer");
          setUserSpecialite(null);
          setLoading(false);
          isInitializedRef.current = false;
        }
      }
    );

    // 🔥 Initialiser APRÈS avoir enregistré le listener
    initializeAuth();

    return () => {
      console.log('🧹 Cleanup useEffect AUTH');
      isMounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [fetchUserRole]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError("");

      console.log("🔐 Tentative login pour:", email);

      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (loginError) {
        console.error("❌ Erreur signInWithPassword:", loginError);
        throw loginError;
      }

      if (data.user) {
        console.log("✅ Connexion réussie:", data.user.id);
        setSession(data.session);
        setUser(data.user);
        setSessionStartTime(new Date());
        
        console.log("🔄 Appel fetchUserRole depuis login...");
        await fetchUserRole(data.user.id);
        console.log("✅ fetchUserRole depuis login terminé");
        
        isInitializedRef.current = true;
      }

      return { success: true, user: data.user };
    } catch (error) {
      const msg = error?.message || "Erreur de connexion";
      console.error("❌ Erreur login:", msg);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  // 🔥 MODIFIÉ: Fonction register simplifiée
  const register = async (email, password, userData = {}) => {
    try {
      setLoading(true);
      setError("");

      console.log('📝 Tentative d\'inscription:', email);
      console.log('📝 Données utilisateur:', userData);

      // 🔥 Inscription avec métadonnées (le trigger créera le profil automatiquement)
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: userData.role || 'viewer',
            nom: userData.nom || '',
            prenom: userData.prenom || '',
            specialite: userData.specialite || null
          }
        }
      });

      if (signUpError) {
        console.error("❌ Erreur signUp:", signUpError);
        throw signUpError;
      }

      console.log("✅ Inscription réussie:", data.user?.id);
      console.log("ℹ️ Le profil sera créé automatiquement par le trigger");

      // 🔥 OPTIONNEL: Attendre un peu que le trigger s'exécute
      // (utile si vous voulez fetch le profil juste après)
      await new Promise(resolve => setTimeout(resolve, 500));

      return { success: true, user: data.user };
    } catch (error) {
      const msg = error?.message || "Erreur d'inscription";
      console.error("❌ Erreur register:", msg);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      console.log("✅ Déconnexion réussie");
      setSession(null);
      setUser(null);
      setUserRole("viewer");
      setUserSpecialite(null);
      setSessionStartTime(null);
      isInitializedRef.current = false;
      return { success: true };
    } catch (error) {
      console.error("❌ Erreur logout:", error);
      setSession(null);
      setUser(null);
      setUserRole("viewer");
      setUserSpecialite(null);
      isInitializedRef.current = false;
      return { success: false, error: error?.message };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      setLoading(true);
      setError("");

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      console.log("✅ Email réinitialisation envoyé");
      return { success: true };
    } catch (error) {
      const msg = error?.message || "Erreur réinitialisation";
      console.error("❌ Erreur resetPassword:", msg);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    try {
      setLoading(true);
      setError("");

      if (updates.email || updates.password) {
        const { error: authError } = await supabase.auth.updateUser(updates);
        if (authError) throw authError;
      }

      if (user?.id) {
        const { error: profileError } = await supabase
          .from("profiles")
          .upsert({
            id: user.id,
            ...updates,
            updated_at: new Date().toISOString(),
          });

        if (profileError) throw profileError;

        // 🔥 AJOUTÉ: Rafraîchir le rôle après mise à jour
        if (updates.role) {
          await fetchUserRole(user.id);
        }
      }

      console.log("✅ Profil mis à jour");
      return { success: true };
    } catch (error) {
      const msg = error?.message || "Erreur mise à jour";
      console.error("❌ Erreur updateProfile:", msg);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    userRole,
    userSpecialite,
    session,
    loading,
    error,
    setError,
    login,
    register,
    logout,
    resetPassword,
    updateProfile,
    isAuthenticated: !!user,
    sessionStartTime,
    supabase,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};